import { type ComponentType, type ReactNode, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useActivateProsProfile,
  useAuth,
  useDeactivateProsProfile,
  useMyProsAnalytics,
  useProviderRegistrationCheckout,
  useProsProfile,
  useRegisterProviderProgram,
  useRecordProviderContact,
  useTenantBootstrap,
  useTenantFamily,
} from '@tark/shared/hooks';
import { Button, Input } from '@tark/shared/components';
import { ProviderRatingLine, ProviderReviewsPanel } from '../components/pros/ProviderReviewsPanel';
import { CARD_CHECKOUT_ENABLED, PAY_OUTSIDE_COPY } from '../lib/cardCheckoutPolicy';
import type {
  ProviderCertification,
  ProviderContactMethod,
  ProviderProfile,
  TenantFamilyAthleteSummary,
  TenantProgram,
  TenantProgramRegistrationCategoryOption,
  TenantProgramRegistrationSessionOption,
} from '@tark/shared/types';
import {
  ArrowLeft,
  Award,
  Building2,
  CheckCircle2,
  Clock3,
  DollarSign,
  Globe,
  Loader2,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  ShieldCheck,
  Sparkles,
  UserRound,
  Wrench,
} from 'lucide-react';

const PUBLICATION_STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Pending review',
  VERIFIED: 'Approved',
  PUBLISHED: 'Live',
  SUSPENDED: 'Suspended',
  ARCHIVED: 'Archived',
};

function formatTypes(profile: ProviderProfile): string {
  return profile.providerTypes
    .map((type) => {
      if (type === 'STRINGER') return 'Stringer';
      if (type === 'ACADEMY') return 'Academy';
      if (type === 'ORGANIZER') return 'Organizer';
      return 'Coach';
    })
    .join(' + ');
}

function formatLocation(profile: ProviderProfile): string {
  if (profile.city && profile.state) return `${profile.city}, ${profile.state}`;
  return profile.city || profile.state || profile.zipcode || 'Area on file';
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'P';
}

function getPrimaryIcon(profile: ProviderProfile) {
  if (profile.providerTypes.includes('ACADEMY')) return Building2;
  if (profile.providerTypes.includes('STRINGER')) return Wrench;
  return UserRound;
}

function getProfileImage(profile: ProviderProfile): string | null {
  return profile.profilePhotoFull640Url || profile.profilePhotoUrl || profile.profilePhotoThumb256Url || null;
}

function money(value?: number | null): string | null {
  if (value === null || value === undefined) return null;
  return `$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function range(min?: number | null, max?: number | null): string | null {
  const minLabel = money(min);
  const maxLabel = money(max);
  if (minLabel && maxLabel && minLabel !== maxLabel) return `${minLabel}-${maxLabel}`;
  return minLabel || maxLabel;
}

function rateSummary(profile: ProviderProfile): { value: string; label: string } | null {
  if (profile.coachDetails) {
    const value = range(profile.coachDetails.hourlyRateMin, profile.coachDetails.hourlyRateMax);
    return value ? { value, label: 'per hour' } : null;
  }
  if (profile.stringerDetails) {
    const value = range(profile.stringerDetails.basePriceMin, profile.stringerDetails.basePriceMax);
    return value ? { value, label: 'labor' } : null;
  }
  return null;
}

function experienceSummary(profile: ProviderProfile): string | null {
  const years =
    profile.coachDetails?.experienceYears ??
    profile.stringerDetails?.experienceYears ??
    null;
  return years ? `${years} years` : null;
}

function areaSummary(profile: ProviderProfile): string {
  if (profile.serviceRadiusMiles) return `${profile.serviceRadiusMiles} mi service area`;
  if (profile.academyDetails?.numCourts) return `${profile.academyDetails.numCourts} courts`;
  return formatLocation(profile);
}

function availabilitySummary(profile: ProviderProfile): string | null {
  if (profile.coachDetails?.availabilityNotes) return profile.coachDetails.availabilityNotes;
  if (profile.stringerDetails?.turnaroundHours) return `${profile.stringerDetails.turnaroundHours} hour turnaround`;
  return null;
}

function specialtyTags(profile: ProviderProfile): string[] {
  const tags = [
    ...(profile.coachDetails?.lessonTypes || []),
    ...(profile.coachDetails?.specialties || []),
    ...(profile.stringerDetails?.stringBrands || []),
    ...(profile.stringerDetails?.offersPickupDelivery ? ['Pickup available'] : []),
    ...(profile.academyDetails?.programsOffered || []),
    ...(profile.academyDetails?.amenities || []),
    ...(profile.sports || []),
  ];
  return Array.from(new Set(tags.filter(Boolean))).slice(0, 10);
}

function certifications(profile: ProviderProfile): ProviderCertification[] {
  return [
    ...(profile.coachDetails?.certifications || []),
    ...(profile.stringerDetails?.certifications || []),
  ];
}

function openTarget(url: string) {
  window.location.href = url;
}

function whatsappUrl(value: string): string {
  const digits = value.replace(/[^\d]/g, '');
  return `https://wa.me/${digits}`;
}

export default function ProsProviderPage() {
  const params = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const providerId = Number(params.providerId);
  const { user } = useAuth();
  const { data: profile, isLoading, isError, refetch } = useProsProfile(providerId, Number.isFinite(providerId));
  const { data: analytics } = useMyProsAnalytics(Boolean(profile && user?.id === profile.userId));
  const { activeTenant, selectTenant, isSwitchingTenant } = useTenantBootstrap(Boolean(user));
  const activateProfile = useActivateProsProfile();
  const deactivateProfile = useDeactivateProsProfile();
  const { mutateAsync: recordContact } = useRecordProviderContact();
  const ownerView = Boolean(profile && user?.id === profile.userId);

  const contactActions = useMemo(() => {
    if (!profile) return [];

    const actions: Array<{
      label: string;
      method: ProviderContactMethod;
      href: string;
      icon: ComponentType<{ className?: string }>;
    }> = [];

    if (profile.contactPhone) {
      actions.push({ label: 'Call', method: 'PHONE', href: `tel:${profile.contactPhone}`, icon: Phone });
    }
    if (profile.contactWhatsapp) {
      actions.push({ label: 'WhatsApp', method: 'WHATSAPP', href: whatsappUrl(profile.contactWhatsapp), icon: MessageCircle });
    }
    if (profile.contactEmail) {
      actions.push({ label: 'Email', method: 'EMAIL', href: `mailto:${profile.contactEmail}`, icon: Mail });
    }
    if (profile.websiteUrl) {
      actions.push({ label: 'Website', method: 'WEBSITE', href: profile.websiteUrl, icon: Globe });
    }

    return actions;
  }, [profile]);

  const handleContact = async (method: ProviderContactMethod, href: string) => {
    if (!profile) return;
    if (profile.allowsPublicContact) {
      try {
        await recordContact({ providerId: profile.id, contactMethod: method });
      } catch {
        // Tracking should never block the actual contact handoff.
      }
    }
    openTarget(href);
  };

  const handleOwnerBack = async () => {
    if (!ownerView) {
      navigate('/pros');
      return;
    }

    const tenantId = profile?.tenantId ?? null;
    if (tenantId && activeTenant?.tenant?.id !== tenantId) {
      await selectTenant({ tenantId });
    }
    navigate('/welcome');
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !profile) {
    return (
      <section className="max-w-xl rounded-lg border border-border bg-white p-5 shadow-sm">
        <p className="text-lg font-semibold text-text">Public Profile unavailable</p>
        <p className="mt-2 text-sm text-text-muted">
          This Public Profile could not be loaded.
        </p>
        <div className="mt-4 flex gap-3">
          <Button onClick={() => refetch()}>Retry</Button>
          <Button variant="outline" onClick={() => navigate('/pros')}>Back to PROs</Button>
        </div>
      </section>
    );
  }

  const PrimaryIcon = getPrimaryIcon(profile);
  const imageUrl = getProfileImage(profile);
  const rate = rateSummary(profile);
  const experience = experienceSummary(profile);
  const availability = availabilitySummary(profile);
  const tags = specialtyTags(profile);
  const certs = certifications(profile);
  const catalogPrograms = (profile.catalogPrograms || []).filter(
    (program) => program.active && program.registrationPolicy?.enabled
  );
  const publicationLabel = PUBLICATION_STATUS_LABEL[profile.publicationStatus || 'DRAFT'] || profile.publicationStatus || 'Draft';
  const primaryContact = contactActions[0];
  const hasOwnerAnalytics = Boolean(
    analytics && (analytics.profileViews > 0 || analytics.contactClicks > 0)
  );

  return (
    <div className="w-full max-w-none space-y-6 pb-24 md:pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-text-muted">
          <button type="button" onClick={() => void handleOwnerBack()} className="transition-colors hover:text-text">
            {ownerView ? 'Coach Studio' : 'PROs'}
          </button>
          <span>/</span>
          <span className="text-text">Public Profile</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="ghost"
            leftIcon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => void handleOwnerBack()}
            isLoading={ownerView && isSwitchingTenant}
          >
            {ownerView ? 'Coach Studio' : 'Back to PROs'}
          </Button>
          {ownerView ? (
            <Button variant="outline" onClick={() => navigate(`/pros/manage?providerId=${providerId}`)}>Manage Profile</Button>
          ) : null}
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px] 2xl:grid-cols-[minmax(0,1fr)_400px]">
        <main className="min-w-0 space-y-6">
          <section className="overflow-hidden rounded-lg border border-border bg-white shadow-sm">
            <div className="grid lg:grid-cols-[minmax(320px,42%)_minmax(0,1fr)]">
              <div className="relative min-h-[420px] bg-surface-elevated">
                {imageUrl ? (
                  <img src={imageUrl} alt={profile.displayName} className="absolute inset-0 h-full w-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-primary/10 text-primary">
                    <span className="text-5xl font-semibold">{getInitials(profile.displayName)}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-col justify-between gap-6 p-5 md:p-6">
                <div>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                      <PrimaryIcon className="h-3.5 w-3.5" />
                      {formatTypes(profile)}
                    </span>
                    {ownerView ? (
                      <span className="rounded-full border border-border bg-surface px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-text-muted">
                        {publicationLabel}
                      </span>
                    ) : null}
                    {profile.verificationLevel === 'PRO' ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                        <Sparkles className="h-3.5 w-3.5" />
                        TARK Reviewed
                      </span>
                    ) : null}
                  </div>
                  <h1 className="mt-4 text-3xl font-semibold tracking-tight text-text md:text-4xl">
                    {profile.displayName}
                  </h1>
                  <p className="mt-3 flex items-center gap-2 text-sm font-medium text-text-muted">
                    <MapPin className="h-4 w-4" />
                    {formatLocation(profile)}
                  </p>
                  <div className="mt-2"><ProviderRatingLine summary={profile.reviewSummary} /></div>

                  {tags.length ? (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <span key={tag} className="rounded-full border border-border bg-surface px-3 py-1 text-xs font-medium text-text-muted">
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <HeroFact icon={DollarSign} label="Rate" value={rate?.value || 'Ask'} sub={rate?.label || 'direct'} />
                  <HeroFact icon={Award} label="Experience" value={experience || 'On file'} />
                  <HeroFact icon={MapPin} label="Area" value={areaSummary(profile)} />
                </div>
              </div>
            </div>
          </section>

          {catalogPrograms.length ? (
            <ClassRegistrationSection
              programs={catalogPrograms}
              profile={profile}
              activeTenantId={activeTenant?.tenant?.id ?? null}
              ownerView={ownerView}
              onManage={() => navigate('/classes')}
            />
          ) : null}

          <section className="rounded-lg border border-border bg-white p-5 shadow-sm md:p-6">
            <SectionTitle eyebrow="About" title={profile.displayName} />
            <p className="mt-4 max-w-4xl text-base leading-7 text-text-muted">
              {profile.bio || profile.academyDetails?.description || `${formatTypes(profile)} based in ${formatLocation(profile)}.`}
            </p>
          </section>

          <ProviderReviewsPanel providerId={profile.id} initialSummary={profile.reviewSummary} />

          <section className="grid gap-6 lg:grid-cols-2">
            {profile.coachDetails ? (
              <DetailPanel title="Lessons">
                <DetailRow label="Rate" value={rate ? `${rate.value} ${rate.label}` : null} />
                <DetailRow label="Lesson types" value={profile.coachDetails.lessonTypes?.join(', ')} />
                <DetailRow label="Specialties" value={profile.coachDetails.specialties?.join(', ')} />
                <DetailRow label="Packages" value={profile.coachDetails.offersPackages ? 'Available' : null} />
                <DetailRow label="Availability" value={profile.coachDetails.availabilityNotes} />
              </DetailPanel>
            ) : null}

            {profile.stringerDetails ? (
              <DetailPanel title="Racquet service">
                <DetailRow label="Labor" value={rate ? `${rate.value} ${rate.label}` : null} />
                <DetailRow label="Turnaround" value={profile.stringerDetails.turnaroundHours ? `${profile.stringerDetails.turnaroundHours} hours` : null} />
                <DetailRow label="String brands" value={profile.stringerDetails.stringBrands?.join(', ')} />
                <DetailRow label="Machines" value={profile.stringerDetails.machinesUsed?.join(', ')} />
                <DetailRow label="Pickup" value={profile.stringerDetails.offersPickupDelivery ? 'Available' : null} />
              </DetailPanel>
            ) : null}

            {profile.academyDetails ? (
              <DetailPanel title="Academy">
                <DetailRow label="Programs" value={profile.academyDetails.programsOffered?.join(', ')} />
                <DetailRow label="Courts" value={profile.academyDetails.numCourts ? `${profile.academyDetails.numCourts}` : null} />
                <DetailRow label="Court types" value={profile.academyDetails.courtTypes?.join(', ')} />
                <DetailRow label="Amenities" value={profile.academyDetails.amenities?.join(', ')} />
                <DetailRow label="Established" value={profile.academyDetails.yearEstablished ? `${profile.academyDetails.yearEstablished}` : null} />
              </DetailPanel>
            ) : null}

            <DetailPanel title="Location">
              <DetailRow label="Area" value={formatLocation(profile)} />
              <DetailRow label="ZIP" value={profile.zipcode} />
              <DetailRow label="Service radius" value={profile.serviceRadiusMiles ? `${profile.serviceRadiusMiles} miles` : null} />
              <DetailRow label="Sports" value={profile.sports?.join(', ')} />
            </DetailPanel>
          </section>

          {certs.length ? (
            <section className="rounded-lg border border-border bg-white p-5 shadow-sm md:p-6">
              <SectionTitle eyebrow="Trust" title="Certifications" />
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {certs.map((cert, index) => (
                  <div key={`${cert.type}-${cert.org}-${index}`} className="rounded-lg border border-border bg-surface px-4 py-3">
                    <p className="font-semibold text-text">{cert.type}</p>
                    <p className="mt-1 text-sm text-text-muted">
                      {[cert.org, cert.level].filter(Boolean).join(' / ') || 'Certification on file'}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </main>

        <aside className="min-w-0 space-y-4 xl:sticky xl:top-6 xl:self-start">
          <section className="rounded-lg border border-border bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-text-subtle">Contact</p>
            {rate ? (
              <div className="mt-3">
                <p className="text-3xl font-semibold tracking-tight text-text">{rate.value}</p>
                <p className="text-sm font-medium text-text-muted">{rate.label}</p>
              </div>
            ) : (
              <p className="mt-3 text-2xl font-semibold tracking-tight text-text">Ask for pricing</p>
            )}
            {availability ? (
              <p className="mt-3 flex gap-2 text-sm text-text-muted">
                <Clock3 className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{availability}</span>
              </p>
            ) : null}

            <div className="mt-5 grid gap-2">
              {primaryContact ? (
                <Button
                  onClick={() => handleContact(primaryContact.method, primaryContact.href)}
                  leftIcon={<primaryContact.icon className="h-4 w-4" />}
                  fullWidth
                >
                  {primaryContact.label}
                </Button>
              ) : null}
              {contactActions.slice(primaryContact ? 1 : 0).map((action) => (
                <Button
                  key={action.method}
                  variant="outline"
                  onClick={() => handleContact(action.method, action.href)}
                  leftIcon={<action.icon className="h-4 w-4" />}
                  fullWidth
                >
                  {action.label}
                </Button>
              ))}
              {!contactActions.length ? (
                <div className="rounded-lg border border-dashed border-border bg-surface px-4 py-3 text-sm font-semibold text-text-muted">
                  Contact not available
                </div>
              ) : null}
            </div>
          </section>

          {ownerView ? (
          <section className="rounded-lg border border-border bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-text-subtle">Profile status</p>
            <div className="mt-4 grid gap-3">
              <StatusLine icon={ShieldCheck} label="Visibility" value={profile.publiclyVisible ? 'Public' : 'Private'} />
              <StatusLine icon={CheckCircle2} label="Review" value={publicationLabel} />
              <StatusLine icon={Award} label="Trust" value={profile.verificationLevel || 'Pending'} />
            </div>
          </section>
          ) : null}

          {ownerView ? (
            <section className="rounded-lg border border-border bg-white p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-text-subtle">Owner tools</p>
              {hasOwnerAnalytics && analytics ? (
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <MetricBox label="Views" value={analytics.profileViews} />
                  <MetricBox label="Contacts" value={analytics.contactClicks} />
                </div>
              ) : null}
              <div className="mt-4 grid gap-2">
                <Button variant="outline" onClick={() => navigate(`/pros/manage?providerId=${providerId}`)} fullWidth>
                  Manage Profile
                </Button>
                {profile.isActive ? (
                  <Button
                    variant="outline"
                    onClick={() => deactivateProfile.mutate()}
                    isLoading={deactivateProfile.isPending}
                    fullWidth
                  >
                    Pause Listing
                  </Button>
                ) : profile.publicationStatus === 'VERIFIED' || profile.publicationStatus === 'PUBLISHED' ? (
                  <Button
                    onClick={() => activateProfile.mutate()}
                    isLoading={activateProfile.isPending}
                    fullWidth
                  >
                    Go Live
                  </Button>
                ) : null}
              </div>
            </section>
          ) : null}

          <section className="rounded-lg border border-border bg-surface p-4 text-sm text-text-muted">
            Contact, pricing, booking, and lesson terms are handled directly with this PRO.
          </section>
        </aside>
      </div>
    </div>
  );
}

function SectionTitle({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-text-subtle">{eyebrow}</p>
      <h2 className="mt-1 text-2xl font-semibold tracking-tight text-text">{title}</h2>
    </div>
  );
}

function ClassRegistrationSection({
  programs,
  profile,
  activeTenantId,
  ownerView,
  onManage,
}: {
  programs: TenantProgram[];
  profile: ProviderProfile;
  activeTenantId: number | null;
  ownerView: boolean;
  onManage: () => void;
}) {
  return (
    <section className="rounded-lg border border-border bg-white p-5 shadow-sm md:p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <SectionTitle eyebrow="Classes" title="Registration" />
        {ownerView ? (
          <Button size="sm" variant="outline" onClick={onManage}>
            Manage Classes
          </Button>
        ) : null}
      </div>
      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        {programs.map((program) => (
          <ClassRegistrationCard
            key={program.programId}
            program={program}
            providerId={profile.id}
            providerTenantId={profile.tenantId ?? null}
            activeTenantId={activeTenantId}
            ownerView={ownerView}
          />
        ))}
      </div>
    </section>
  );
}

function ClassRegistrationCard({
  program,
  providerId,
  providerTenantId,
  activeTenantId,
  ownerView,
}: {
  program: TenantProgram;
  providerId: number;
  providerTenantId: number | null;
  activeTenantId: number | null;
  ownerView: boolean;
}) {
  const { user } = useAuth();
  const policy = program.registrationPolicy;
  const sessionOptions = policy?.sessionOptions?.filter((option) => (option.status || 'OPEN').toUpperCase() !== 'CLOSED') ?? [];
  const categoryOptions = policy?.categoryOptions?.filter((option) => (option.status || 'OPEN').toUpperCase() !== 'CLOSED') ?? [];
  const [sessionId, setSessionId] = useState(sessionOptions[0]?.optionId || sessionOptions[0]?.label || '');
  const [categoryId, setCategoryId] = useState(categoryOptions[0]?.optionId || categoryOptions[0]?.label || '');
  const [selectedAthleteId, setSelectedAthleteId] = useState('');
  const [studentName, setStudentName] = useState('');
  const [daysPreference, setDaysPreference] = useState('');
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [registrationResult, setRegistrationResult] = useState<{
    registrationId: number;
    status: string;
    paymentStatus: string;
    balanceAmount?: number | null;
    externalPaymentUrl?: string | null;
    rosterBookingCount?: number | null;
    waitlistBookingCount?: number | null;
  } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const registerProgram = useRegisterProviderProgram(providerId);
  const providerCheckout = useProviderRegistrationCheckout(providerId);
  const canLoadHousehold = Boolean(user && !ownerView && providerTenantId && activeTenantId === providerTenantId);
  const { household } = useTenantFamily(canLoadHousehold);
  const athletes = household?.athletes ?? [];
  const selectedSession = findRegistrationOption(sessionOptions, sessionId);
  const selectedCategory = findRegistrationOption(categoryOptions, categoryId);
  const selectedAthleteIdNumber = selectedAthleteId ? Number(selectedAthleteId) : null;
  const priceLabel = selectedCategory
    ? formatCategoryPrice(selectedCategory, policy?.currencyCode)
    : policy?.priceLabel || null;
  const dateLabel = buildProgramDateLabel(program);
  const requiresStudent = policy?.requireStudentName !== false;
  const requiresDays = Boolean(policy?.requireDaysPreference);
  const availability = buildSelectedAvailability(program, selectedSession, selectedCategory);
  const canSubmit =
    !ownerView &&
    waiverAccepted &&
    (!requiresStudent || Boolean(selectedAthleteIdNumber) || studentName.trim().length > 1) &&
    (!requiresDays || daysPreference.trim().length > 1);

  const handleRegister = async () => {
    if (!canSubmit) {
      return;
    }
    setFormError(null);
    try {
      const response = await registerProgram.mutateAsync({
        programId: program.programId,
        athleteId: selectedAthleteIdNumber,
        sessionOptionRecordId: selectedSession?.recordId ?? null,
        categoryOptionRecordId: selectedCategory?.recordId ?? null,
        sessionOptionId: selectedSession?.optionId || selectedSession?.label || null,
        categoryOptionId: selectedCategory?.optionId || selectedCategory?.label || null,
        studentFullName: selectedAthleteIdNumber ? null : studentName.trim(),
        daysPreference: daysPreference.trim() || null,
        quantity: 1,
        waiverAccepted,
      });
      setRegistrationResult({
        registrationId: response.registrationId,
        status: response.registrationStatus,
        paymentStatus: response.paymentStatus,
        balanceAmount: response.balanceAmount,
        externalPaymentUrl: response.externalPaymentUrl,
        rosterBookingCount: response.rosterBookingCount,
        waitlistBookingCount: response.waitlistBookingCount,
      });
    } catch (error) {
      console.error('Failed to register provider program', error);
      setFormError('Registration could not be saved. Try again or contact the coach.');
    }
  };
  const canPayRegistration = Boolean(
    registrationResult && (CARD_CHECKOUT_ENABLED || Boolean(registrationResult.externalPaymentUrl)) &&
      (registrationResult.balanceAmount ?? 0) > 0 &&
      !['PAID', 'WAIVED', 'REFUNDED', 'VOID'].includes((registrationResult.paymentStatus || '').toUpperCase())
  );

  const handlePayRegistration = async () => {
    if (!registrationResult) {
      return;
    }
    setCheckoutError(null);
    try {
      if (registrationResult.externalPaymentUrl) {
        window.location.assign(registrationResult.externalPaymentUrl);
        return;
      }
      const response = await providerCheckout.mutateAsync({
        registrationId: registrationResult.registrationId,
        payload: buildCheckoutReturnUrls(),
      });
      if (response.checkoutUrl) {
        window.location.assign(response.checkoutUrl);
      }
    } catch (error) {
      console.error('Failed to create registration checkout', error);
      setCheckoutError('Payment checkout is not available right now. Try again or contact the coach.');
    }
  };

  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-lg font-semibold tracking-tight text-text">{policy?.publicTitle || program.name}</p>
          {policy?.registrationNote || program.description ? (
            <p className="mt-1 line-clamp-2 text-sm leading-6 text-text-muted">
              {policy?.registrationNote || program.description}
            </p>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2">
            {dateLabel ? <MiniFact label="Dates" value={dateLabel} /> : null}
            {policy?.locationLabel ? <MiniFact label="Location" value={policy.locationLabel} /> : null}
            {policy?.audienceLabel ? <MiniFact label="For" value={policy.audienceLabel} /> : null}
          </div>
        </div>
        {priceLabel ? (
          <div className="shrink-0 text-right">
            <p className="text-lg font-semibold text-text">{priceLabel}</p>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle">USD</p>
            {availability ? <AvailabilityPill availability={availability} align="right" /> : null}
          </div>
        ) : null}
      </div>

      <div className="mt-4 grid gap-3">
        {sessionOptions.length ? (
          <SelectField
            label="Session"
            value={sessionId}
            onChange={setSessionId}
            options={sessionOptions.map((option) => ({
              value: option.optionId || option.label,
              label: [option.label, option.dateLabel || buildOptionDateLabel(option), option.availabilityLabel]
                .filter(Boolean)
                .join(' - '),
            }))}
          />
        ) : null}
        {categoryOptions.length ? (
          <SelectField
            label="Category"
            value={categoryId}
            onChange={setCategoryId}
            options={categoryOptions.map((option) => ({
              value: option.optionId || option.label,
              label: `${[option.label, option.ageLabel, option.levelLabel, option.coachLabel].filter(Boolean).join(' / ')}${formatCategoryPrice(option, policy?.currencyCode) ? ` - ${formatCategoryPrice(option, policy?.currencyCode)}` : ''}${option.availabilityLabel ? ` - ${option.availabilityLabel}` : ''}`,
            }))}
          />
        ) : null}
        {availability && !priceLabel ? <AvailabilityPill availability={availability} /> : null}
        {selectedCategory?.coachLabel ? <InfoLine label="Coach" value={selectedCategory.coachLabel} /> : null}
        {selectedCategory?.scheduleLabel ? (
          <InfoLine label="Time" value={selectedCategory.scheduleLabel} />
        ) : selectedSession?.scheduleLabel ? (
          <InfoLine label="Time" value={selectedSession.scheduleLabel} />
        ) : null}
        {selectedCategory?.daysRule ? <InfoLine label="Days" value={selectedCategory.daysRule} /> : null}
        {athletes.length ? (
          <SelectField
            label="Student"
            value={selectedAthleteId}
            onChange={setSelectedAthleteId}
            options={[
              { value: '', label: 'New student' },
              ...athletes.map((athlete) => ({
                value: String(athlete.athleteId),
                label: formatAthleteName(athlete),
              })),
            ]}
          />
        ) : null}
        {requiresStudent && !selectedAthleteIdNumber ? (
          <Input
            label={athletes.length ? 'New student name' : 'Student'}
            placeholder="Student full name"
            value={studentName}
            onChange={(event) => setStudentName(event.target.value)}
          />
        ) : null}
        {requiresDays ? (
          <Input
            label="Days"
            placeholder="Mon/Wed, Tue/Thu, or 4 days"
            value={daysPreference}
            onChange={(event) => setDaysPreference(event.target.value)}
          />
        ) : null}
        <label className="flex items-start gap-2 rounded-lg border border-border bg-white px-3 py-3">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
            checked={waiverAccepted}
            onChange={(event) => setWaiverAccepted(event.target.checked)}
          />
          <span className="text-sm font-medium leading-6 text-text-muted">
            I accept the academy waiver for this student.
          </span>
        </label>
        {registrationResult ? (
          <div className="grid gap-2 rounded-lg border border-primary/20 bg-primary/10 px-3 py-3 text-sm font-semibold text-primary sm:grid-cols-3">
            <span>{formatStatusLabel(registrationResult.status)}</span>
            <span>{registrationResult.rosterBookingCount ?? 0} roster</span>
            <span>{formatStatusLabel(registrationResult.paymentStatus)}</span>
          </div>
        ) : null}
        {registrationResult && canPayRegistration ? (
          <Button
            size="sm"
            leftIcon={<DollarSign className="h-4 w-4" />}
            isLoading={providerCheckout.isPending}
            disabled={providerCheckout.isPending}
            onClick={() => void handlePayRegistration()}
          >
            Pay now
          </Button>
        ) : null}
        {registrationResult && !CARD_CHECKOUT_ENABLED && !registrationResult.externalPaymentUrl && (registrationResult.balanceAmount ?? 0) > 0 ? (
          <p className="text-sm leading-6 text-text-muted">{PAY_OUTSIDE_COPY}</p>
        ) : null}
        {checkoutError ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700">
            {checkoutError}
          </p>
        ) : null}
        {formError ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700">
            {formError}
          </p>
        ) : null}
      </div>

      <Button
        className="mt-4"
        fullWidth
        disabled={!canSubmit || registerProgram.isPending || Boolean(registrationResult)}
        isLoading={registerProgram.isPending}
        onClick={() => void handleRegister()}
      >
        {ownerView
          ? 'Owner Preview'
          : registrationResult
            ? formatStatusLabel(registrationResult.status)
            : availability?.status === 'WAITLIST'
              ? 'Join Waitlist'
              : 'Request Spot'}
      </Button>
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <label className="space-y-1.5">
      <span className="block text-sm font-medium text-text">{label}</span>
      <select
        className="w-full rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm font-medium text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function InfoLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-white px-3 py-2">
      <span className="text-xs font-semibold uppercase tracking-[0.14em] text-text-subtle">{label}</span>
      <span className="text-sm font-semibold text-text">{value}</span>
    </div>
  );
}

function MiniFact({ label, value }: { label: string; value: string }) {
  return (
    <span className="rounded-full border border-border bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-text-subtle">
      {label}: <span className="text-text">{value}</span>
    </span>
  );
}

type CatalogAvailability = {
  status: 'OPEN' | 'LIMITED' | 'WAITLIST';
  label: string;
  openSpotCount?: number | null;
  waitlistCount?: number | null;
};

type CatalogAvailabilityOption = {
  availabilityStatus?: string | null;
  availabilityLabel?: string | null;
  openSpotCount?: number | null;
  waitlistCount?: number | null;
};

function AvailabilityPill({
  availability,
  align = 'left',
}: {
  availability: CatalogAvailability;
  align?: 'left' | 'right';
}) {
  const tone =
    availability.status === 'WAITLIST'
      ? 'border-amber-200 bg-amber-50 text-amber-700'
      : availability.status === 'LIMITED'
        ? 'border-orange-200 bg-orange-50 text-orange-700'
        : 'border-primary/20 bg-primary/10 text-primary';

  return (
    <span
      className={[
        'mt-1 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]',
        tone,
        align === 'right' ? 'justify-end' : '',
      ].join(' ')}
    >
      {availability.label}
    </span>
  );
}

function buildSelectedAvailability(
  program: TenantProgram,
  selectedSession: TenantProgramRegistrationSessionOption | null,
  selectedCategory: TenantProgramRegistrationCategoryOption | null
): CatalogAvailability | null {
  const options = [selectedCategory, selectedSession].filter(Boolean) as CatalogAvailabilityOption[];
  const measured = options.filter(
    (option) =>
      option.availabilityStatus ||
      option.availabilityLabel ||
      typeof option.openSpotCount === 'number' ||
      typeof option.waitlistCount === 'number'
  );
  if (!measured.length) {
    return program.defaultCapacity ? { status: 'OPEN', label: `${program.defaultCapacity} spots` } : null;
  }

  const optionsWithOpenCount = measured.filter((option) => typeof option.openSpotCount === 'number');
  const tightest = optionsWithOpenCount.sort((left, right) => Number(left.openSpotCount) - Number(right.openSpotCount))[0];
  const waitlistCount = measured.reduce((max, option) => Math.max(max, Number(option.waitlistCount ?? 0)), 0);
  const hasWaitlistStatus = measured.some(
    (option) => (option.availabilityStatus || '').toUpperCase() === 'WAITLIST' || option.openSpotCount === 0
  );
  const hasLimitedStatus = measured.some((option) => (option.availabilityStatus || '').toUpperCase() === 'LIMITED');

  if (hasWaitlistStatus) {
    return {
      status: 'WAITLIST',
      label: waitlistCount > 0 ? 'Waitlist open' : 'Join waitlist',
      openSpotCount: 0,
      waitlistCount,
    };
  }

  const label =
    tightest?.availabilityLabel ||
    measured.find((option) => option.availabilityLabel)?.availabilityLabel ||
    (typeof tightest?.openSpotCount === 'number'
      ? `${tightest.openSpotCount} ${tightest.openSpotCount === 1 ? 'spot' : 'spots'} left`
      : 'Open');
  return {
    status: hasLimitedStatus ? 'LIMITED' : 'OPEN',
    label,
    openSpotCount: tightest?.openSpotCount ?? null,
    waitlistCount,
  };
}

function formatAthleteName(athlete: TenantFamilyAthleteSummary): string {
  const name = athlete.preferredName || [athlete.givenName, athlete.familyName].filter(Boolean).join(' ');
  const details = [athlete.levelCode ? formatStatusLabel(athlete.levelCode) : null, athlete.birthDate || null].filter(Boolean);
  return details.length ? `${name} - ${details.join(' / ')}` : name;
}

function findRegistrationOption<T extends { optionId?: string | null; label: string }>(
  options: T[],
  selectedId: string
): T | null {
  return options.find((option) => (option.optionId || option.label) === selectedId) || options[0] || null;
}

function buildProgramDateLabel(program: TenantProgram): string | null {
  const policy = program.registrationPolicy;
  if (policy?.seasonLabel) {
    return policy.seasonLabel;
  }
  if (policy?.startsOn && policy?.endsOn) {
    return `${formatShortCatalogDate(policy.startsOn)} - ${formatShortCatalogDate(policy.endsOn)}`;
  }
  return null;
}

function buildOptionDateLabel(option: TenantProgramRegistrationSessionOption): string | null {
  if (option.startsOn && option.endsOn) {
    return `${formatShortCatalogDate(option.startsOn)} - ${formatShortCatalogDate(option.endsOn)}`;
  }
  return null;
}

function formatShortCatalogDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) {
    return value;
  }
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatCategoryPrice(
  option: TenantProgramRegistrationCategoryOption,
  currencyCode?: string | null
): string | null {
  if (option.priceLabel) {
    return option.priceLabel;
  }
  if (option.priceAmount === null || option.priceAmount === undefined) {
    return null;
  }
  return Number(option.priceAmount).toLocaleString('en-US', {
    style: 'currency',
    currency: currencyCode || 'USD',
    maximumFractionDigits: 0,
  });
}

function formatStatusLabel(value?: string | null): string {
  if (!value) return 'Pending';
  return value
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function buildCheckoutReturnUrls() {
  const fallbackUrl = 'https://playtark.com/family';
  const currentUrl = typeof window === 'undefined' ? fallbackUrl : window.location.href;
  return {
    successUrl: currentUrl,
    cancelUrl: currentUrl,
  };
}

function HeroFact({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: typeof DollarSign;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-surface px-4 py-3">
      <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </p>
      <p className="mt-1 truncate text-sm font-semibold text-text">{value}</p>
      {sub ? <p className="text-xs text-text-muted">{sub}</p> : null}
    </div>
  );
}

function DetailPanel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-white p-5 shadow-sm">
      <h2 className="text-lg font-semibold tracking-tight text-text">{title}</h2>
      <div className="mt-4 divide-y divide-border">{children}</div>
    </section>
  );
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="grid gap-2 py-3 sm:grid-cols-[140px_minmax(0,1fr)]">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-text-subtle">{label}</p>
      <p className="text-sm font-medium leading-6 text-text">{value}</p>
    </div>
  );
}

function StatusLine({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof ShieldCheck;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-surface px-3 py-2">
      <span className="inline-flex items-center gap-2 text-sm font-medium text-text-muted">
        <Icon className="h-4 w-4 text-primary" />
        {label}
      </span>
      <span className="text-sm font-semibold text-text">{value}</span>
    </div>
  );
}

function MetricBox({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle">{label}</p>
      <p className="mt-1 text-lg font-semibold text-text">{value}</p>
    </div>
  );
}
